import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LayoutTemplate, Plus, Users, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import DashboardLayout from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { jsonApi } from '@/lib/jsonApi';
import DuplicateTemplate from '@/components/taxonomy/DuplicateTemplate';
import { plural, templateCounts } from '@/components/taxonomy/counts';

export const Availability = ({ t }) => (t.available
  ? <span className="text-[10px] font-bold uppercase tracking-wide text-[#1F684A] bg-[#EDF8F2] border border-[#CDE9DA] rounded-full px-2 py-0.5" data-testid={`template-available-${t.template_id}`}>Available to clients</span>
  : <span className="text-[10px] font-bold uppercase tracking-wide text-[#92400E] bg-[#FFFBEB] border border-[#FCD34D] rounded-full px-2 py-0.5" data-testid={`template-unavailable-${t.template_id}`}>{t.status === 'active' ? 'Not available — no active factors' : 'Inactive'}</span>);

function TemplateCard({ t, index, onOpen, onDuplicate }) {
  return (
    <div className="tx-reveal bg-white border border-[#E2E8F0] rounded-2xl p-5 flex flex-col" style={{ animationDelay: `${Math.min(index, 8) * 50}ms` }} data-testid={`template-card-${t.template_id}`}>
      <div className="flex items-start justify-between gap-2">
        <p className="font-bold text-[#0F172A] font-['Montserrat'] text-lg leading-tight">{t.name}</p>
        <Availability t={t} />
      </div>
      {t.description && <p className="text-xs text-[#64748B] mt-1">{t.description}</p>}
      <p className="text-sm text-[#334155] mt-3">{templateCounts(t)}</p>
      <p className="text-xs text-[#94A3B8] mt-1 flex items-center gap-1"><Users className="w-3 h-3" /> {plural(t.clients_using, 'client')} started from this template</p>
      <div className="flex gap-2 mt-auto pt-4">
        <Button size="sm" onClick={() => onOpen(t.template_id)} className="bg-[#1A3E6F] hover:bg-[#15325A]" data-testid={`template-edit-${t.template_id}`}>Edit <ChevronRight className="w-3.5 h-3.5 ml-1" /></Button>
        <DuplicateTemplate template={t} onDuplicate={onDuplicate} testId={`template-duplicate-${t.template_id}`} />
      </div>
    </div>
  );
}

export default function TaxonomyTemplates() {
  const navigate = useNavigate();
  const [list, setList] = useState(null);
  const [error, setError] = useState(null);
  const [name, setName] = useState('');
  const load = useCallback(() => jsonApi('/platform/taxonomy-templates').then((d) => setList(d.templates)).catch(setError), []);
  useEffect(() => { load(); }, [load]);
  const open = (id) => navigate(`/platform/taxonomy-templates/${id}`);
  const create = async (e) => {
    e.preventDefault();
    try { const d = await jsonApi('/platform/taxonomy-templates', 'POST', { name: name.trim() }); toast.success('Template created'); open(d.template_id); }
    catch (err) { toast.error(err.message); }
  };
  const duplicate = async (id, newName) => {
    try { const d = await jsonApi(`/platform/taxonomy-templates/${id}/duplicate`, 'POST', { name: newName }); toast.success('Duplicated as an inactive draft — activate it when ready'); open(d.template_id); return true; }
    catch (err) { toast.error(err.message); return false; }
  };
  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto space-y-6" data-testid="taxonomy-templates-page">
        <header className="space-y-2">
          <p className="text-[11px] font-bold uppercase tracking-[0.12em] text-[#1A3E6F] flex items-center gap-1.5"><LayoutTemplate className="w-3.5 h-3.5" /> satisFIND master data</p>
          <h1 className="text-4xl sm:text-5xl font-bold text-[#0F172A] font-['Montserrat'] tracking-tight">Attribute &amp; Factor templates</h1>
          <p className="text-sm sm:text-base text-[#475569] max-w-3xl">Industry starting points clients can pick once. Choosing a template gives the client an independent copy, so editing a template here never changes existing clients. A template is offered to clients only when it has active factors.</p>
        </header>
        {error && <p className="text-sm text-[#A1272E] bg-[#FFF0F0] border border-[#F0C6C9] rounded-xl px-4 py-3" data-testid="taxonomy-templates-error">{error.message}</p>}
        {!error && (
          <form onSubmit={create} className="bg-white border border-dashed border-[#CBD5E1] rounded-2xl p-4 flex flex-col sm:flex-row gap-3" data-testid="create-template-form">
            <Input value={name} onChange={(e) => setName(e.target.value)} maxLength={80} placeholder="New template, e.g. Quick-Service Restaurant" className="flex-1" data-testid="create-template-name" />
            <Button type="submit" disabled={!name.trim()} className="bg-[#1A3E6F] hover:bg-[#15325A]" data-testid="create-template-submit"><Plus className="w-4 h-4 mr-1.5" /> Create template</Button>
          </form>
        )}
        {!list && !error && <div className="py-16 text-center"><div className="loading-spinner mx-auto" /></div>}
        {list && (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {list.map((t, i) => <TemplateCard key={t.template_id} t={t} index={i} onOpen={open} onDuplicate={duplicate} />)}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
